/**
 * Summary Prompt — Constructs LLM prompts for generating scientific
 * narrative summaries from workflow provenance data.
 *
 * Serializes provenance steps and workspace context into a structured
 * format, manages token budget, and provides grounding instructions
 * to prevent hallucination.
 *
 * @module gibbs/workflow/summaryPrompt
 */

import { getAction } from '../registry/index';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Maximum tokens for the provenance + workspace context in the user message. */
const PROVENANCE_TOKEN_BUDGET = 6000;

/** Steps to keep in full detail at each end when truncating. */
const KEEP_STEPS = 3;

/** Step count threshold above which truncation kicks in. */
const TRUNCATION_THRESHOLD = 10;

// ─── System Prompt ──────────────────────────────────────────────────────────

/**
 * Build the system prompt for summary generation.
 *
 * @returns {string}
 */
export function buildSystemPrompt() {
    return [
        'You are a scientific report writer for computational drug discovery workflows.',
        'Given a numbered list of workflow steps (with parameters, timing, status, and results)',
        'and workspace context (proteins, compounds, computed properties),',
        'write a concise scientific narrative summary in Markdown.',
        '',
        'Structure your response with exactly these four sections using ## headers:',
        '',
        '## Objective',
        'State the goal of the workflow based on the steps performed.',
        '',
        '## Methods',
        'Describe the computational methods used, referencing specific tools,',
        'parameters, and sequence of operations.',
        '',
        '## Results',
        'Report quantitative outcomes — energies, scores, counts, durations.',
        'Include any errors or failed steps.',
        '',
        '## Key Findings',
        'Summarize the most important findings and conclusions from the results.',
        '',
        'IMPORTANT GROUNDING RULES:',
        '- Only report values that appear in the provenance data below.',
        '- Do not infer, estimate, or fabricate any numerical results, energies, scores, or identifiers.',
        '- If a result summary is missing or a step failed, say so explicitly.',
        '- Reference PDB IDs, compound names, and scores exactly as they appear in the data.',
        '- Do not invent compound names, protein names, or binding affinities.',
    ].join('\n');
}

// ─── Provenance Serialization ───────────────────────────────────────────────

/**
 * Format a duration in milliseconds to a human-readable string.
 *
 * @param {number|null} ms
 * @returns {string}
 */
function formatDuration(ms) {
    if (ms == null) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Summarize a result object into a short string.
 *
 * @param {Object|null} resultSummary
 * @returns {string}
 */
function summarizeResult(resultSummary) {
    if (!resultSummary) return 'No result recorded';
    if (typeof resultSummary === 'string') return resultSummary;

    // Build a readable summary from key-value pairs
    const parts = [];
    for (const [key, value] of Object.entries(resultSummary)) {
        if (value == null) continue;
        if (typeof value === 'object') {
            parts.push(`${key}: ${JSON.stringify(value)}`);
        } else {
            parts.push(`${key}: ${value}`);
        }
    }
    return parts.length > 0 ? parts.join(', ') : 'No result details';
}

/**
 * Format parameter values into a readable string.
 *
 * @param {Object} params
 * @returns {string}
 */
function formatParams(params) {
    if (!params || Object.keys(params).length === 0) return 'none';
    return Object.entries(params)
        .map(([k, v]) => {
            if (typeof v === 'string') return `${k} = "${v}"`;
            return `${k} = ${JSON.stringify(v)}`;
        })
        .join(', ');
}

/**
 * Serialize a single step record to a full-detail text block.
 *
 * @param {import('./provenance.js').StepRecord} step
 * @param {Object} [registry] - Optional registry for looking up action names
 * @returns {string}
 */
function serializeStepFull(step, registry) {
    const actionDef = registry ? getActionFromRegistry(step.actionId, registry) : null;
    const actionName = actionDef ? actionDef.name : step.actionId;

    const lines = [
        `Step ${step.stepIndex + 1}: ${actionName} (${step.actionId})`,
        `  Parameters: ${formatParams(step.params)}`,
        `  Duration: ${formatDuration(step.durationMs)} | Status: ${step.status || 'UNKNOWN'}`,
    ];

    if (step.error) {
        lines.push(`  Error: ${step.error}`);
    }

    lines.push(`  Result: ${summarizeResult(step.resultSummary)}`);

    // Data lineage
    if (step.inputSources && step.inputSources.length > 0) {
        const lineageStrs = step.inputSources.map(
            (s) => `Step ${s.sourceStepIndex + 1} (${s.outputName} → ${s.inputName})`
        );
        lines.push(`  Input from: ${lineageStrs.join(', ')}`);
    }

    return lines.join('\n');
}

/**
 * Serialize a step to a one-line summary (for truncated middle steps).
 *
 * @param {import('./provenance.js').StepRecord} step
 * @param {Object} [registry]
 * @returns {string}
 */
function serializeStepBrief(step, registry) {
    const actionDef = registry ? getActionFromRegistry(step.actionId, registry) : null;
    const actionName = actionDef ? actionDef.name : step.actionId;
    return `Step ${step.stepIndex + 1}: ${actionName} — ${step.status || 'UNKNOWN'}`;
}

/**
 * Look up an action definition from a registry object or the global registry.
 *
 * @param {string} actionId
 * @param {Object} [registry] - Object with getAction(id) or the global getAction
 * @returns {Object|undefined}
 */
function getActionFromRegistry(actionId, registry) {
    if (registry && typeof registry.getAction === 'function') {
        return registry.getAction(actionId);
    }
    // Fall back to imported global getAction
    return getAction(actionId);
}

/**
 * Serialize a ProvenanceLog into a structured text context for the LLM prompt.
 *
 * For workflows with more than TRUNCATION_THRESHOLD steps, keeps the first
 * KEEP_STEPS and last KEEP_STEPS in full detail, and summarizes the middle.
 *
 * @param {import('./provenance.js').ProvenanceLog} provenance
 * @param {Object} [registry] - Registry object with getAction(), or uses global
 * @returns {string}
 */
export function serializeProvenance(provenance, registry) {
    const steps = provenance.getSteps();
    if (steps.length === 0) {
        return 'No workflow steps were recorded.';
    }

    const totalDuration = steps.reduce((sum, s) => sum + (s.durationMs || 0), 0);
    const header = `Workflow Execution: ${steps.length} steps, total duration: ${formatDuration(totalDuration)}`;

    let stepLines;

    if (steps.length <= TRUNCATION_THRESHOLD) {
        // All steps in full detail
        stepLines = steps.map((s) => serializeStepFull(s, registry));
    } else {
        // Truncate: first N, summary, last N
        const firstSteps = steps.slice(0, KEEP_STEPS);
        const lastSteps = steps.slice(-KEEP_STEPS);
        const middleSteps = steps.slice(KEEP_STEPS, steps.length - KEEP_STEPS);

        const middleDuration = middleSteps.reduce((sum, s) => sum + (s.durationMs || 0), 0);
        const middleFailed = middleSteps.filter((s) => s.status === 'FAILED').length;

        const middleSummaryParts = [
            `... ${middleSteps.length} intermediate steps`,
            `(total duration: ${formatDuration(middleDuration)}`,
        ];
        if (middleFailed > 0) {
            middleSummaryParts.push(`${middleFailed} failed`);
        }

        // Brief listing of middle step names
        const middleBriefs = middleSteps.map((s) => serializeStepBrief(s, registry));

        stepLines = [
            ...firstSteps.map((s) => serializeStepFull(s, registry)),
            '',
            `${middleSummaryParts.join(' ') }) ...`,
            ...middleBriefs,
            '',
            ...lastSteps.map((s) => serializeStepFull(s, registry)),
        ];
    }

    let result = `${header }\n\n${ stepLines.join('\n\n')}`;

    // Token budget check — further truncate middle briefs if needed
    if (estimateTokenCount(result) > PROVENANCE_TOKEN_BUDGET
        && steps.length > TRUNCATION_THRESHOLD) {
        // Remove individual middle step briefs, keep only the count summary
        const firstSteps = steps.slice(0, KEEP_STEPS);
        const lastSteps = steps.slice(-KEEP_STEPS);
        const middleSteps = steps.slice(KEEP_STEPS, steps.length - KEEP_STEPS);
        const middleDuration = middleSteps.reduce((sum, s) => sum + (s.durationMs || 0), 0);

        result = [
            header,
            '',
            ...firstSteps.map((s) => serializeStepFull(s, registry)),
            '',
            `... ${middleSteps.length} intermediate steps (total duration: ${formatDuration(middleDuration)}) ...`,
            '',
            ...lastSteps.map((s) => serializeStepFull(s, registry)),
        ].join('\n\n');
    }

    return result;
}

// ─── Workspace Context Serialization ────────────────────────────────────────

/**
 * Serialize workspace state into a compact context string for the prompt.
 *
 * Extracts protein name/PDB ID, compound names, and key computed properties
 * (energies, ADMET, docking scores).
 *
 * @param {Object} workspace - BMaps workspace state
 * @returns {string}
 */
export function serializeWorkspaceContext(workspace) {
    const sections = [];

    // Proteins. Correct API is allCaseData(); getCaseDatas does not
    // exist so the optional chaining was silently returning []
    // and the workflow summary prompt was missing protein context.
    const caseDatas = workspace.allCaseData?.() || [];
    const proteins = [];
    for (const cd of caseDatas) {
        const mapCase = cd.mapCase;
        if (mapCase) {
            const pdbId = mapCase.pdbId || mapCase.name || 'unknown';
            const name = mapCase.displayName || mapCase.name || '';
            proteins.push(name ? `${name} (PDB: ${pdbId})` : `PDB: ${pdbId}`);
        }
    }
    if (proteins.length > 0) {
        sections.push(`Proteins: ${proteins.join(', ')}`);
    }

    // Compounds with properties. Correct API is getLoadedCompounds().
    const compounds = workspace.getLoadedCompounds?.() || [];
    if (compounds.length > 0) {
        const compoundLines = [];
        for (let i = 0; i < compounds.length; i++) {
            const c = compounds[i];
            const name = c.name || `Compound_${i + 1}`;
            const props = [];

            // Energies
            const energies = c.getEnergies?.();
            if (energies) {
                if (energies.totalEnergy != null) props.push(`total energy: ${energies.totalEnergy}`);
                if (energies.bindingEnergy != null) props.push(`binding energy: ${energies.bindingEnergy}`);
                if (energies.vanDerWaals != null) props.push(`vdW: ${energies.vanDerWaals}`);
                if (energies.electrostatic != null) props.push(`electrostatic: ${energies.electrostatic}`);
            }

            // Docking score
            const dockingScore = c.getDockingScore?.();
            if (dockingScore != null) {
                props.push(`docking score: ${dockingScore}`);
            }

            // ADMET properties
            const admet = c.getAdmetProperties?.();
            if (admet) {
                for (const [key, value] of Object.entries(admet)) {
                    if (value != null) props.push(`${key}: ${value}`);
                }
            }

            const propStr = props.length > 0 ? ` [${props.join('; ')}]` : '';
            compoundLines.push(`  ${i + 1}. ${name}${propStr}`);
        }
        sections.push(`Compounds:\n${compoundLines.join('\n')}`);
    }

    // Active compound
    const active = workspace.getActiveCompound?.();
    if (active) {
        const idx = compounds.indexOf(active);
        if (idx >= 0) {
            sections.push(`Active compound: ${active.name || `Compound_${idx + 1}`}`);
        }
    }

    if (sections.length === 0) {
        return 'No workspace context available.';
    }

    return `Workspace Context:\n${ sections.join('\n')}`;
}

// ─── Token Budget ───────────────────────────────────────────────────────────

/**
 * Estimate token count for a string (~4 characters per token).
 *
 * @param {string|Object} input - String or object to estimate
 * @returns {number}
 */
export function estimateTokenCount(input) {
    const text = typeof input === 'string' ? input : JSON.stringify(input);
    return Math.ceil(text.length / 4);
}

/**
 * Build the complete messages array for the summary LLM call.
 *
 * @param {import('./provenance.js').ProvenanceLog} provenance
 * @param {Object} [workspace] - BMaps workspace state
 * @param {Object} [registry] - Registry with getAction()
 * @returns {Array<{role: string, content: string}>}
 */
export function buildMessages(provenance, workspace, registry) {
    const systemPrompt = buildSystemPrompt();
    const provenanceContext = serializeProvenance(provenance, registry);

    const userParts = ['# Workflow Provenance Data', '', provenanceContext];

    if (workspace) {
        const workspaceContext = serializeWorkspaceContext(workspace);
        userParts.push('', `# ${ workspaceContext.split('\n')[0]}`, '', workspaceContext);
    }

    userParts.push('', 'Please generate a scientific summary of this workflow execution.');

    return [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userParts.join('\n') },
    ];
}
